// [S-383/S-385] Draft editor + workspace. Reuses the CKEditor-backed
// TemplateEditor for content, saves immutable versions, runs reviews against
// the certified templates + precedents, and lets the user triage each
// finding (apply / ignore). Owners can share with same-tenant collaborators
// at read or write permission. All access control is enforced server-side;
// the UI gates affordances for clarity, not security.
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DraftActionsMenu } from "../components/drafts/DraftActionsMenu";
import {
  Alert,
  Button,
  Dialog,
  DialogClose,
  EditorFrame,
  LoadingState,
  PaginatedDocument,
  Pill,
  Select,
  useToast,
  type EditorFrameProps,
} from "../components";
import { ApiError, getApiErrorMessage } from "../data/api";
import { useSession } from "../auth/AuthProvider";
import {
  useDraft,
  useDraftReviews,
  useDraftVersion,
  useDraftVersions,
  useRestoreVersion,
  useSaveVersion,
  useShareDraft,
  useUnshareDraft,
  type Draft,
  type DraftCitation,
  type DraftReview,
  type DraftReviewFinding,
  type DraftSharePermission,
  type DraftVersion,
} from "../data/draftsQuery";
import type { ReviewPrecedent } from "../data/reviewsQuery";
import {
  useChatSessionDetail,
  useCreateChatSession,
  type RecordRef,
} from "../data/chatQuery";
import { useAgentDraftLock } from "./agents/agentDraftLock";
import { useSplitDrag } from "./useSplitDrag";
import { useReviewFocusScroll } from "./agents/DraftRailEditor";
import {
  isWorkspaceSessionFor,
  workspaceOpenRecord,
  WORKSPACE_CHAT_KIND,
} from "./agents/workspaceChat";
import { RecordDrawerProvider, useRecordDrawer } from "./agents/RecordDrawer";
import { useTenantUsersList } from "../data/tenantsQuery";
import { useAdminTemplate } from "../data/templatesQuery";
// [S-517] Reuses the SAME ChatPane Home renders — already proven portable —
// instead of building a second chat surface for the Draft Workspace's right
// rail.
import { ChatPane } from "./agents/ChatPane";
import { markCitations } from "../components/markCitations";

// [S-413 follow-up] Optional id prop lets the record drawer embed this view,
// mirroring PrecedentDetail/TemplateDetail; with no prop it reads the route
// param as before. `embedded` renders a lightweight READ-ONLY preview (title
// + chips + current version content via the shared PaginatedDocument widget)
// instead of mounting the full CKEditor Editor — the editor's save/review/
// share/version-history machinery belongs on the full-page route ("open in
// new tab"), not a drawer preview.
export function DraftDetail({
  id: idProp,
  embedded = false,
}: { id?: string; embedded?: boolean } = {}): JSX.Element {
  const params = useParams<{ draftId: string }>();
  const draftId = idProp ?? params.draftId;
  const { data: draft, isLoading, isError, error } = useDraft(draftId);

  if (isLoading) {
    return (
      <div className="tpl__state" role="status" aria-live="polite">
        Loading draft…
      </div>
    );
  }
  if (isError) {
    const isNotFound = error instanceof ApiError && error.status === 403;
    return (
      <Alert
        label={isNotFound ? "Draft not available" : "Couldn't load draft"}
        tone="error"
      >
        {isNotFound ? (
          <>
            This draft doesn't exist or isn't shared with you.{" "}
            <Link to="/drafts">Back to drafts</Link>.
          </>
        ) : (
          error.message
        )}
      </Alert>
    );
  }
  if (draft === undefined) return <></>;

  return embedded ? <DraftPreview draft={draft} /> : <Editor draft={draft} />;
}

/** Lightweight read-only preview for the record drawer — same shared
 *  PaginatedDocument widget the Templates/Precedents preview uses, so
 *  browsing a template, a precedent, and a drafted contract all present
 *  content through one consistent widget. No editing affordances; "Open in
 *  new tab" (rendered by RecordDrawer) is the path into the full editor.
 *
 *  [citation-gap fix, found live 2026-08-14] Diego, on the earlier chat-
 *  citation fix: "that's not what I meant by citations, I meant the draft
 *  generated does not show citations when opened in the main chat!" — this
 *  is that gap. The full-page editor (Editor, below) loads
 *  `currentVersion.data?.citations` and marks them via CKEditor's
 *  citationsToggle plugin (TemplateEditor.ckeditor.tsx); this embedded
 *  preview never did either — it rendered the SAME version's raw content
 *  through PaginatedDocument with no citation awareness at all, so opening
 *  a just-generated draft from the Home rail showed a citation-free
 *  document even though the citations existed in the exact same
 *  DraftVersion this preview already fetches. markCitations (S-715) is a
 *  pure, engine-agnostic HTML transform with zero CKEditor dependency —
 *  built specifically to be usable outside the editor — so this reuses it
 *  directly instead of pulling in CKEditor for a read-only preview. Click
 *  handling mirrors TemplateEditor.ckeditor.tsx's own DOM listener
 *  (closest(".cs-citation") -> data-citation-idx -> citations[idx]) as a
 *  plain React onClick, since there's no CKEditor root to attach to here. */
function DraftPreview({ draft }: { draft: Draft }): JSX.Element {
  const hasVersion = draft.currentVersion > 0;
  const version = useDraftVersion(draft.id, hasVersion ? draft.currentVersion : undefined);
  const { openRecord } = useRecordDrawer();
  const citations = version.data?.citations ?? [];
  const content = useMemo(
    () => markCitations(version.data?.content ?? "", citations),
    [version.data?.content, citations]
  );
  const onClickCitation = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      const mark = (e.target as HTMLElement).closest<HTMLElement>(".cs-citation");
      if (mark === null) return;
      const idx = Number(mark.dataset["citationIdx"]);
      const c = citations[idx];
      if (c !== undefined) openRecord(c.citation.recordRef, c.citation.quote);
    },
    [citations, openRecord]
  );

  return (
    <div className="dft-preview">
      <header className="dft-preview__hero">
        <div className="dft-preview__title">{draft.title}</div>
        <div className="dft-preview__chips">
          <span className="chip">{prettyDealType(draft.dealType)}</span>
          <span className="chip">{prettyGuild(draft.guild)}</span>
          <span className="chip">
            {hasVersion ? `v${draft.currentVersion}` : "No content yet"}
          </span>
        </div>
      </header>
      {!hasVersion ? (
        <div className="tpl__state">This draft has no saved content yet.</div>
      ) : version.isLoading ? (
        <div className="tpl__state" role="status" aria-live="polite">
          Loading draft content…
        </div>
      ) : version.isError ? (
        <Alert label="Couldn't load this draft's content" tone="error">
          {version.error.message}
        </Alert>
      ) : (
        <div onClick={onClickCitation}>
          <PaginatedDocument content={content} testId="dft-doc" />
        </div>
      )}
    </div>
  );
}

/** [S-715] Thin wrapper resolving citation clicks to the record drawer.
 *  `Editor` (below) renders `<RecordDrawerProvider>` as part of its OWN
 *  output, so a `useRecordDrawer()` call in `Editor` itself would see the
 *  no-op fallback — provider context only flows to descendants of where the
 *  provider appears in the tree, not to the component that renders it. This
 *  component IS such a descendant (mirrors FindingRow's own working call
 *  further down), so the hook resolves correctly here. */
function DraftEditorFrame(
  props: Omit<EditorFrameProps, "onOpenCitation">
): JSX.Element {
  const { openRecord } = useRecordDrawer();
  return (
    <EditorFrame
      {...props}
      onOpenCitation={(c) => openRecord(c.citation.recordRef, c.citation.quote)}
    />
  );
}

function Editor({ draft }: { draft: Draft }): JSX.Element {
  const session = useSession();
  const isOwner = draft.ownerUid === session?.uid;
  const myCollab = draft.sharedWith.find((c) => c.uid === session?.uid);
  const canWrite = isOwner || myCollab?.permission === "write";

  const navigate = useNavigate();

  // Seed the editor from the current version's content. CKEditor only reads
  // its `data` prop at mount, and the version content arrives asynchronously —
  // so we gate the editor's render on the content being loaded and key it by
  // the loaded version, forcing a fresh mount (with the right initial content)
  // on first load and after a restore.
  const hasVersion = draft.currentVersion > 0;
  const currentVersion = useDraftVersion(
    draft.id,
    hasVersion ? draft.currentVersion : undefined
  );
  const toast = useToast();
  // The CKEditor instance, captured via onReady — used to locate a clause via
  // the editor's model (`find` command) rather than scraping the DOM.
  const editorRef = useRef<CkEditorLike | null>(null);
  const ready = !hasVersion || currentVersion.data !== undefined;
  const loadedVersion = hasVersion ? (currentVersion.data?.version ?? 0) : 0;
  const [content, setContent] = useState("");
  // [S-715] Seeded alongside content, from the same loaded version — a
  // best-effort visual layer describing what was true at generation/save
  // time, not kept in sync with ad-hoc edits (applyFinding etc.) afterward;
  // the NEXT saved version gets its own accurate citations server-side (S-714).
  const [citations, setCitations] = useState<DraftCitation[]>([]);
  // Seed synchronously during render (React's "adjust state when inputs
  // change" pattern) so the editor mounts with the right content on the same
  // commit — an effect would run after the editor already mounted blank.
  const [seededVersion, setSeededVersion] = useState<number | null>(null);
  // True once the user has typed since the last seed/save. The review walkthrough
  // writes NEW server versions (apply-as-you-go); a newer version replaces the
  // editor only when this is false, otherwise the user chooses (keep mine / take new).
  const [dirty, setDirty] = useState(false);
  const newerAvailable = ready && seededVersion !== null && seededVersion !== loadedVersion;
  if (ready && (seededVersion === null || (newerAvailable && !dirty))) {
    setSeededVersion(loadedVersion);
    setContent(currentVersion.data?.content ?? "");
    setCitations(currentVersion.data?.citations ?? []);
  }
  const needsChoice = newerAvailable && dirty;
  const keepMine = (): void => setSeededVersion(loadedVersion);
  const takeNew = (): void => {
    setSeededVersion(loadedVersion);
    setContent(currentVersion.data?.content ?? "");
    setCitations(currentVersion.data?.citations ?? []);
    setDirty(false);
  };
  // Read-only while the agent may be writing this draft (a turn is running or
  // the draft is building); what the user typed stays and saves once it lifts.
  const lock = useAgentDraftLock(draft.id);
  const writable = canWrite && lock === null;
  // Card -> document focus from the workspace chat: scroll + outline the clause
  // under review in THIS editor (the same subscription the Home rail uses).
  const hostRef = useReviewFocusScroll(draft.id);
  const onEdit = (next: string): void => {
    setContent(next);
    setDirty(true);
  };

  // Scroll the editor to the finding's clause. Prefer the editor's own model
  // (the `find` command from FindAndReplace) — it searches the document text
  // robustly and highlights the match. Falls back to a DOM text scan if the
  // editor instance or find command isn't available. Returns false when the
  // clause can't be located (e.g. a "missing" finding with no draft text).
  const jumpToFinding = useCallback(
    (finding: DraftReviewFinding): boolean => {
      const fragments = searchFragments(finding);
      if (fragments.length > 0) {
        if (findInEditor(editorRef.current, fragments)) return true;
        if (findInDom(fragments)) return true;
      }
      toast.push({
        variant: "info",
        title: "Couldn't locate this in the draft",
        description: "The wording may differ — scroll to the section manually.",
      });
      return false;
    },
    [toast]
  );

  const save = useSaveVersion();
  const [shareOpen, setShareOpen] = useState(false);
  // Collapse the whole side rail to give the editor full width (S-398).
  const [panelsOpen, setPanelsOpen] = useState(true);
  // Collapse the entire rail down to a compact handle (panels fully hidden,
  // column shrinks so the editor reclaims the width) — S-398.
  const [railOpen, setRailOpen] = useState(true);
  // [S-517] The right rail's TWO tabs — "Panels" (Version History/Reviews,
  // the pre-existing content) and "Chat" (this draft's conversation). Chat is
  // ALWAYS the default tab, however the draft was reached — asking a question
  // about the open draft is the more common next step than checking version
  // history. A `?chat=<sessionId>` URL param — set by generate_draft/
  // update_draft's "Open in Draft Workspace" link (chatAgentTools.ts) — opens
  // that SAME conversation instead of an empty "Start chat" prompt, but
  // either way the rail lands on Chat, not Panels.
  const [searchParams] = useSearchParams();
  const chatFromUrl = searchParams.get("chat");
  const [railMode, setRailMode] = useState<"panels" | "chat">("chat");
  // Document | chat split: the chat opens at 40% of the width (document 60%) and the
  // user can drag the divider; double-click resets. Same logic as Home's chat |
  // document split (useSplitDrag). Only active in side-by-side layout (wide
  // viewport) with the Chat tab open — the 340px Panels tab and the stacked/mobile
  // layout keep their own widths.
  const splitRef = useRef<HTMLDivElement | null>(null);
  // [chat height 2026-10-15] In Chat mode the rail takes exactly the height
  // left below it in the window, measured, so the composer is always on
  // screen. The CSS fallback (`calc(100vh - 180px)`) guesses the header's
  // height; the real header (breadcrumb, title, meta, the panel toggle) is
  // ~260px at laptop size, which left the composer 79px below the fold until
  // the page was scrolled — and the rail's overscroll containment kept the
  // wheel from scrolling the page while the pointer was over the chat. Live
  // on Bill's laptop: "when the chat gets to the bottom I can't get there".
  // Re-measured on page scroll (the rail is sticky, so its top moves up to
  // the scrollport and the height grows to match) and on resize.
  // A callback ref, not an effect: the rail mounts after the draft loads, and
  // an effect keyed on the rail's mode would have run (and found nothing)
  // before that. The ref fires on the node itself, whenever it attaches, and
  // with null when it detaches — which is the cleanup. (No unmount effect for
  // that: StrictMode's dev double-mount ran one, tore the listeners down, and
  // refs are not re-invoked after it.)
  const sideFitCleanup = useRef<(() => void) | null>(null);
  const sideRef = useCallback(
    (el: HTMLElement | null) => {
      sideFitCleanup.current?.();
      sideFitCleanup.current = null;
      if (el === null || railMode !== "chat") return;
      const scroller = el.closest(".main__body");
      const fit = (): void => {
        const top = el.getBoundingClientRect().top;
        el.style.height = `${String(Math.max(240, Math.floor(window.innerHeight - top - 16)))}px`;
      };
      fit();
      scroller?.addEventListener("scroll", fit, { passive: true });
      window.addEventListener("resize", fit);
      const observer = typeof ResizeObserver === "function" ? new ResizeObserver(fit) : null;
      if (observer !== null && el.parentElement !== null) observer.observe(el.parentElement);
      sideFitCleanup.current = () => {
        scroller?.removeEventListener("scroll", fit);
        window.removeEventListener("resize", fit);
        observer?.disconnect();
        el.style.height = "";
      };
    },
    [railMode]
  );
  const [wideLayout, setWideLayout] = useState(
    () => typeof window.matchMedia !== "function" || window.matchMedia("(min-width: 981px)").matches
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(min-width: 981px)");
    const on = (): void => setWideLayout(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const splitActive = panelsOpen && railOpen && railMode === "chat" && wideLayout;
  const { sideWidth: chatWidth, gap: splitGap, handleProps: splitHandleProps } = useSplitDrag({
    containerRef: splitRef,
    defaultFraction: 0.4,
    enabled: splitActive,
  });
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  const createChat = useCreateChatSession();
  const handleStartChat = useCallback(() => {
    createChat.mutate(
      { agentKind: WORKSPACE_CHAT_KIND, tenantId: draft.tenantId, draftId: draft.id },
      { onSuccess: (session) => setChatSessionId(session.id) }
    );
  }, [createChat, draft.tenantId, draft.id]);
  // A restored `?chat=` session is reused only if it was opened FROM this draft.
  // A Home chat (or another draft's) is not adopted: a fresh workspace session
  // is created instead, so the chat can never be about some other record.
  const restored = useChatSessionDetail(chatFromUrl, draft.tenantId);
  const restoreVerdict =
    chatFromUrl === null
      ? "none"
      : restored.isLoading
        ? "pending"
        : restored.data === undefined
          ? "unavailable"
          : isWorkspaceSessionFor(restored.data.session, draft.id)
            ? "reuse"
            : "foreign";
  const autoStarted = useRef(false);
  useEffect(() => {
    if (restoreVerdict !== "foreign" || autoStarted.current) return;
    autoStarted.current = true;
    handleStartChat();
  }, [restoreVerdict, handleStartChat]);
  const activeChatId = chatSessionId ?? (restoreVerdict === "reuse" ? chatFromUrl : null);
  const chatResolving =
    activeChatId === null &&
    (restoreVerdict === "pending" || (restoreVerdict === "foreign" && !createChat.isError));
  const openRecordRef = useMemo(() => workspaceOpenRecord(draft.id), [draft.id]);
  // Per-panel vertical collapse for granular control while the rail is open.
  // Version history starts collapsed.
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [legacyOpen, setLegacyOpen] = useState(false);

  function handleSave(): void {
    if (!writable || save.isPending) return;
    save.mutate(
      { draftId: draft.id, content },
      { onSuccess: () => setDirty(false) }
    );
  }

  return (
    <RecordDrawerProvider>
      <div className="dft__detail__head">
        <div className="dft__detail__title">
          <div className="page__head__eyebrow">
            <Link to="/drafts">← Drafts</Link>
          </div>
          <h1>{draft.title || "Untitled draft"}</h1>
          <div className="page__head__sub">
            {prettyGuild(draft.guild)} · {prettyDealType(draft.dealType)} · v
            {draft.currentVersion} · {isOwner ? "Owner" : myCollab?.permission ?? "read"}
          </div>
        </div>
        <div className="dft__detail__actions">
          {isOwner && (
            <Button variant="ghost" onClick={() => setShareOpen(true)}>
              Share
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!writable || save.isPending}
          >
            {save.isPending ? "Saving…" : "Save version"}
          </Button>
          <DraftActionsMenu
            draft={draft}
            isOwner={isOwner}
            onDeleted={() => navigate("/drafts")}
          />
        </div>
      </div>

      {save.isError && (
        <Alert label="Couldn't save" tone="error">
          {getApiErrorMessage(save.error) ?? save.error.message}
        </Alert>
      )}

      {/* [UX follow-up] Rendered outside the conditionally-mounted <aside>
          below (never inside it) so the control to bring the panel back
          survives panelsOpen going false — and sits right above the panel
          it toggles instead of across the page next to Save/Share. */}
      <div className="dft__detail__panel-toggle">
        <Button
          variant="ghost"
          onClick={() => setPanelsOpen((o) => !o)}
          aria-expanded={panelsOpen}
          aria-controls="dft-side-panels"
          title={
            panelsOpen
              ? "Hide the side panel for a wider editor"
              : "Show version history, reviews & chat"
          }
        >
          {panelsOpen ? "Hide side panel" : "Show side panel"}
        </Button>
      </div>

      <div
        ref={splitRef}
        style={
          splitActive && chatWidth !== undefined
            ? { gridTemplateColumns: `minmax(0, 1fr) ${chatWidth}px` }
            : undefined
        }
        className={`dft__detail__split${
          !panelsOpen
            ? " dft__detail__split--full"
            : !railOpen
              ? " dft__detail__split--rail-collapsed"
              : railMode === "chat"
                ? " dft__detail__split--chat"
                : ""
        }`}
      >
        {splitActive && chatWidth !== undefined && (
          <div
            className="dft__split-handle"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize chat (double-click to reset to 60/40)"
            title="Drag to resize · double-click to reset"
            data-testid="dft-split-handle"
            style={{ right: chatWidth + splitGap / 2 - 7 }}
            {...splitHandleProps}
          />
        )}
        <div className="dft__detail__main" ref={hostRef}>
          {!canWrite && (
            <div className="dft__readonly" role="note">
              You have read-only access to this draft.
            </div>
          )}
          {canWrite && lock !== null && (
            <div className="dft__readonly" role="note" data-testid="draft-agent-lock">
              {lock}
            </div>
          )}
          {needsChoice && (
            <div className="dft__readonly dft__newer" role="alert" data-testid="draft-newer-version">
              <span>
                A newer version (v{loadedVersion}) was saved while you had unsaved edits.
              </span>
              <span className="dft__newer__actions">
                <Button variant="ghost" onClick={keepMine}>
                  Keep mine
                </Button>
                <Button variant="secondary" onClick={takeNew}>
                  Take new
                </Button>
              </span>
            </div>
          )}
          {ready ? (
            <DraftEditorFrame
              key={`${seededVersion ?? 0}`}
              bounded
              value={content}
              onChange={onEdit}
              readOnly={!writable}
              ariaLabel="Draft content editor"
              placeholder="Paste or type the contract draft…"
              pagerMeta={writable ? "Draft · deal document" : "Read-only"}
              citations={citations}
              onReady={(editor) => {
                editorRef.current = editor as CkEditorLike;
              }}
            />
          ) : (
            <div className="tpl-editor__skeleton" aria-busy="true">
              Loading draft content…
            </div>
          )}
        </div>
        {panelsOpen && (
          <aside
            id="dft-side-panels"
            ref={sideRef}
            key={railMode}
            className={`dft__detail__side${
              railOpen ? "" : " dft__detail__side--collapsed"
            }${railMode === "chat" ? " dft__detail__side--chat" : ""}`}
          >
            <div className="dft__side__bar">
              {/* [S-517] Panels (the pre-existing Version History + Reviews)
                  vs. Chat (this draft's conversation) — two views of the same
                  rail, not stacked, so the rail's width stays the panels'
                  340px in either mode rather than fighting for space. */}
              <div className="dft__side__tabs" role="tablist" aria-label="Draft workspace panel">
                <Button
                  variant="bare"
                  role="tab"
                  aria-selected={railMode === "panels"}
                  className={`dft__side__tab${railMode === "panels" ? " is-active" : ""}`}
                  onClick={() => setRailMode("panels")}
                >
                  Panels
                </Button>
                <Button
                  variant="bare"
                  role="tab"
                  aria-selected={railMode === "chat"}
                  className={`dft__side__tab${railMode === "chat" ? " is-active" : ""}`}
                  onClick={() => setRailMode("chat")}
                  data-testid="dft-tab-chat"
                >
                  Chat
                </Button>
              </div>
              {railMode === "panels" && (
                <Button
                  variant="bare"
                  className="dft__side__bar__toggle"
                  onClick={() => setRailOpen((o) => !o)}
                  aria-expanded={railOpen}
                  aria-controls="dft-side-panels"
                  title={railOpen ? "Collapse panels" : "Expand panels"}
                >
                  <Chevron open={railOpen} />
                </Button>
              )}
            </div>
            {railMode === "panels" ? (
              railOpen && (
                <>
                  <VersionHistory
                    draft={draft}
                    canWrite={canWrite}
                    open={versionsOpen}
                    onToggle={() => setVersionsOpen((o) => !o)}
                  />
                  <LegacyReviewsPanel
                    draft={draft}
                    open={legacyOpen}
                    onToggle={() => setLegacyOpen((o) => !o)}
                    onJump={jumpToFinding}
                  />
                </>
              )
            ) : (
              <div className="dft__side__chat" data-testid="dft-chat-panel">
                {activeChatId !== null ? (
                  <ChatPane
                    sessionId={activeChatId}
                    tenantId={draft.tenantId}
                    openRecordRef={openRecordRef}
                    draftHost
                  />
                ) : chatResolving ? (
                  <LoadingState label="Opening conversation…" />
                ) : (
                  <div className="dft__side__chat-empty">
                    <p>Start a conversation about this draft — ask questions, or refine it by chatting.</p>
                    <Button
                      variant="primary"
                      onClick={handleStartChat}
                      disabled={createChat.isPending}
                    >
                      {createChat.isPending ? "Starting…" : "Start chat"}
                    </Button>
                    {createChat.isError && (
                      <Alert label="Couldn't start chat" tone="error">
                        {getApiErrorMessage(createChat.error) ?? createChat.error.message}
                      </Alert>
                    )}
                  </div>
                )}
              </div>
            )}
          </aside>
        )}
      </div>

      {isOwner && (
        <ShareModal draft={draft} open={shareOpen} onOpenChange={setShareOpen} />
      )}

    </RecordDrawerProvider>
  );
}

// Collapsible panel heading: a chevron toggle that hides the panel body so
// the rail can be trimmed down without hiding it entirely (S-398).
function PanelHeader({
  title,
  open,
  onToggle,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <Button
      variant="bare"
      className="dft__panel__title dft__panel__title--toggle"
      onClick={onToggle}
      aria-expanded={open}
      style={{ marginBottom: open ? undefined : 0 }}
    >
      <Chevron open={open} />
      <span>{title}</span>
    </Button>
  );
}

function VersionHistory({
  draft,
  canWrite,
  open,
  onToggle,
}: {
  draft: Draft;
  canWrite: boolean;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  const { data, isLoading } = useDraftVersions(draft.id);
  const restore = useRestoreVersion();
  const versions = data ?? [];

  return (
    <section className="dft__panel">
      <PanelHeader title="Version history" open={open} onToggle={onToggle} />
      {open && (
        <>
          {isLoading && <LoadingState label="Loading version history…" />}
          {!isLoading && versions.length === 0 && (
            <div className="dft__panel__empty">
              No versions yet. Save to create v1.
            </div>
          )}
          {versions.length > 0 && (
            <ol className="dft__versions">
              {versions.map((v) => (
                <VersionRow
                  key={v.version}
                  version={v}
                  isCurrent={v.version === draft.currentVersion}
                  canWrite={canWrite}
                  onRestore={() =>
                    restore.mutate({ draftId: draft.id, version: v.version })
                  }
                  restoring={restore.isPending}
                />
              ))}
            </ol>
          )}
        </>
      )}
    </section>
  );
}

function VersionRow({
  version,
  isCurrent,
  canWrite,
  onRestore,
  restoring,
}: {
  version: DraftVersion;
  isCurrent: boolean;
  canWrite: boolean;
  onRestore: () => void;
  restoring: boolean;
}): JSX.Element {
  return (
    <li className="dft__version">
      <div className="dft__version__main">
        <span className="dft__version__num">v{version.version}</span>
        <span className="dft__version__date">
          {formatDateTime(version.createdAt)}
        </span>
        {isCurrent && <Pill variant="low">current</Pill>}
      </div>
      {canWrite && !isCurrent && (
        <Button variant="ghost" onClick={onRestore} disabled={restoring}>
          Restore
        </Button>
      )}
    </li>
  );
}

type FindingHandlers = {
  onJump: (finding: DraftReviewFinding) => boolean;
};

// Earlier reviews (legacy). "Save & review" is gone — the clause walkthrough in
// the chat is the one review path — but drafts reviewed the old way keep their
// stored findings. Read-only: no Apply/Undo/triage, Jump still works. Renders
// nothing when the draft has none. Delete this panel (and FindingRow/ReviewBlock)
// once legacy reviews are no longer worth showing.
export function LegacyReviewsPanel({
  draft,
  open,
  onToggle,
  onJump,
}: {
  draft: Draft;
  open: boolean;
  onToggle: () => void;
} & FindingHandlers): JSX.Element | null {
  const { data } = useDraftReviews(draft.id);
  const reviews = data ?? [];
  if (reviews.length === 0) return null;
  return (
    <section className="dft__panel" data-testid="legacy-reviews">
      <PanelHeader title="Earlier reviews (legacy)" open={open} onToggle={onToggle} />
      {open &&
        reviews.map((r) => (
          <ReviewBlock
            key={r.id}
            tenantId={draft.tenantId}
            review={r}
            onJump={onJump}
          />
        ))}
    </section>
  );
}

function ReviewBlock({
  tenantId,
  review,
  onJump,
}: {
  tenantId: string;
  review: DraftReview;
} & FindingHandlers): JSX.Element {
  const open = review.deviations.filter((d) => d.status === "open").length;
  // Never show the raw templateId — resolve to the template's title (or a
  // readable guild/deal-type fallback for legacy rows with no title).
  const anchorTemplate = useAdminTemplate(
    review.templateId ?? undefined,
    tenantId
  ).data;
  const anchorLabel =
    anchorTemplate === undefined
      ? "…"
      : (anchorTemplate.title?.trim() ?? "").length > 0
        ? anchorTemplate.title
        : `${anchorTemplate.guild} ${anchorTemplate.dealType.replace(/_/g, " ")}`;
  return (
    <div className="dft__review">
      <div className="dft__review__head">
        <span className="dft__review__ver">v{review.version}</span>
        <span className="dft__review__date">
          {formatDateTime(review.createdAt)}
        </span>
        <span className="dft__review__meta">
          {review.deviations.length} finding
          {review.deviations.length === 1 ? "" : "s"}
          {open > 0 ? ` · ${open} open` : " · all triaged"}
        </span>
      </div>
      {review.templateId !== null && (
        <div className="dft__review__anchor">
          Anchored to <b>{anchorLabel}</b>
        </div>
      )}
      {review.deviations.length === 0 ? (
        <div className="dft__panel__empty">
          No deviations — the draft matches the certified standard.
        </div>
      ) : (
        <div className="dft__findings">
          <FindingsLegend />
          {review.deviations.map((f) => (
            <FindingRow
              key={f.findingId}
              finding={f}
              precedents={review.precedents}
              tenantId={tenantId}
              onJump={onJump}
            />
          ))}
        </div>
      )}
      {review.precedents.length > 0 && (
        <div className="dft__review__prec">
          <span className="src-dot stud" aria-hidden="true" />
          {review.precedents.length} similar precedent
          {review.precedents.length === 1 ? "" : "s"}
        </div>
      )}
    </div>
  );
}

// [S-510] Plain-language key for the finding labels (Bill §8: "refresher needed
// on finding ranking"). Collapsed by default so it never clutters the panel.
function FindingsLegend(): JSX.Element {
  return (
    <details className="dft__legend">
      <summary className="dft__legend__summary">What do these labels mean?</summary>
      <dl className="dft__legend__list">
        <div className="dft__legend__row">
          <dt>
            <Pill variant="high">high</Pill> <Pill variant="medium">medium</Pill>{" "}
            <Pill variant="low">low</Pill>
          </dt>
          <dd>Risk — how much this deviation matters if left as-is.</dd>
        </div>
        <div className="dft__legend__row">
          <dt>missing</dt>
          <dd>A standard clause is absent — add it to the draft.</dd>
        </div>
        <div className="dft__legend__row">
          <dt>added</dt>
          <dd>An extra clause not in the standard — consider removing it.</dd>
        </div>
        <div className="dft__legend__row">
          <dt>substantive</dt>
          <dd>The clause exists but drifts from the standard — replace it.</dd>
        </div>
      </dl>
    </details>
  );
}

function FindingRow({
  finding,
  precedents,
  tenantId,
  onJump,
}: {
  finding: DraftReviewFinding;
  precedents: ReviewPrecedent[];
  tenantId: string;
} & FindingHandlers): JSX.Element {
  const { openRecord } = useRecordDrawer();
  // Findings that were triaged under the old flow start collapsed.
  const [expanded, setExpanded] = useState(finding.status === "open");
  const applied = finding.status === "apply";

  // Jumpable whenever there's something to search for (a draft clause excerpt
  // or a section label) — not merely when kind !== "missing". A finding can be
  // tagged "missing" yet still point at a placeholder line that exists in the
  // draft (e.g. "Per diem: [TBD]"), which should still be clickable.
  const canJump = searchFragments(finding).length > 0;
  // [S-360b] Precedent-grounded context for this finding (if the synthesis step
  // found a comparable precedent). The cited ids index into the review's
  // precedents — resolve them to openable cards; an uncited note still shows.
  const ctx = finding.precedentContext ?? null;
  const citedPrecedents =
    ctx !== null && ctx.citedPrecedentIds.length > 0
      ? precedents.filter((p) => ctx.citedPrecedentIds.includes(p.id))
      : [];
  return (
    <div className={`dft__finding dft__finding--${finding.status}`}>
      <div className="dft__finding__head">
        <Button
          variant="bare"
          className="dft__finding__toggle"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse finding" : "Expand finding"}
        >
          <Chevron open={expanded} />
        </Button>
        <Pill variant={finding.riskLevel}>
          {finding.riskLevel} · {finding.kind}
        </Pill>
        {finding.section !== undefined && finding.section.length > 0 && (
          <span className="dft__finding__section">{finding.section}</span>
        )}
        <StatusBadge status={finding.status} />
      </div>

      {expanded && (
        <>
          <Button
            variant="bare"
            className={
              canJump
                ? "dft__finding__body dft__finding__body--jumpable"
                : "dft__finding__body"
            }
            onClick={() => {
              if (canJump) onJump(finding);
            }}
            title={canJump ? "Jump to this clause in the draft" : undefined}
          >
            <span className="dft__finding__reason">{finding.reason}</span>
          </Button>
          {canJump ? (
            <div className="dft__finding__hint">Click to find this clause in the draft →</div>
          ) : applied && finding.kind === "added" ? (
            <div className="dft__finding__hint">Removed from the draft.</div>
          ) : (
            <div className="dft__finding__hint">Not in the draft.</div>
          )}
          {ctx !== null && ctx.note.length > 0 && (
            <div className="dft__finding__prec">
              <div className="dft__finding__prec__label">
                <span className="src-dot stud" aria-hidden="true" />
                Precedent context
              </div>
              <p className="dft__finding__prec__note">{ctx.note}</p>
              {citedPrecedents.length > 0 && (
                <div className="dft__finding__prec__cites">
                  {citedPrecedents.map((p) => (
                    <Button
                      key={p.id}
                      variant="bare"
                      className="dft__finding__prec__chip"
                      onClick={() => openRecord(precedentRef(p, tenantId))}
                      title={p.subtitle ?? "Open precedent"}
                    >
                      {p.title ?? p.id}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Build a RecordRef for a cited precedent inline — importing makeRecordRef as a
// VALUE from @csai/core would pull the core barrel (audit/normalize → node:crypto)
// into the browser bundle and break `vite build`. Mirrors recordUri for the
// precedent case.
function precedentRef(p: ReviewPrecedent, tenantId: string): RecordRef {
  const tid = tenantId.length > 0 ? tenantId : null;
  return {
    type: "precedent",
    id: p.id,
    tenantId: tid,
    title: p.title ?? p.id,
    ...(p.subtitle !== undefined ? { subtitle: p.subtitle } : {}),
    ...(p.summary !== undefined ? { summary: p.summary } : {}),
    uri: `csai://precedent/${tid ?? "global"}/${p.id}`,
  };
}

function Chevron({ open }: { open: boolean }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}

function StatusBadge({ status }: { status: DraftReviewFinding["status"] }): JSX.Element | null {
  if (status === "open") return null;
  return (
    <span className={`dft__status dft__status--${status}`}>
      {status === "apply" ? "Apply" : "Ignored"}
    </span>
  );
}

const PERMISSIONS: { value: DraftSharePermission; label: string }[] = [
  { value: "read", label: "Read" },
  { value: "write", label: "Write" },
];

function ShareModal({
  draft,
  open,
  onOpenChange,
}: {
  draft: Draft;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const share = useShareDraft();
  const unshare = useUnshareDraft();
  const [uid, setUid] = useState("");
  const [permission, setPermission] = useState<DraftSharePermission>("read");
  // Collaborators are always same-tenant — resolve via the tenant roster
  // (has persisted displayName/email) rather than showing the raw uid.
  const tenantUsers = useTenantUsersList(draft.tenantId).data?.users ?? [];
  const collaboratorLabel = (collaboratorUid: string): string => {
    const u = tenantUsers.find((row) => row.uid === collaboratorUid);
    return u?.displayName ?? u?.email ?? "—";
  };

  function handleShare(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (uid.trim().length === 0 || share.isPending) return;
    share.mutate(
      { draftId: draft.id, uid: uid.trim(), permission },
      { onSuccess: () => setUid("") }
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Share draft"
      description="Share with a member of your organization. Read lets them view; write lets them edit and triage."
    >
      <form onSubmit={handleShare} className="dft__form">
        <label className="dft__field">
          <span className="dft__field__lbl">Collaborator UID</span>
          <input
            className="dft__input"
            value={uid}
            onChange={(e) => setUid(e.target.value)}
            placeholder="Identity Platform UID"
            autoFocus
          />
        </label>
        <label className="dft__field">
          <span className="dft__field__lbl">Permission</span>
          <Select
            label="Permission"
            value={permission}
            clearable={false}
            onValueChange={(v) => v !== undefined && setPermission(v)}
            options={PERMISSIONS}
          />
        </label>
        {share.isError && (
          <div className="prec__state" role="alert">
            {getApiErrorMessage(share.error) ?? share.error.message}
          </div>
        )}
        <div className="dft__form__actions">
          <Button
            type="submit"
            variant="primary"
            disabled={uid.trim().length === 0 || share.isPending}
          >
            {share.isPending ? "Sharing…" : "Add collaborator"}
          </Button>
        </div>
      </form>

      <div className="dft__collabs">
        <div className="dft__panel__title">Collaborators</div>
        {draft.sharedWith.length === 0 ? (
          <div className="dft__panel__empty">Owner-only. No collaborators.</div>
        ) : (
          <ul className="dft__collabs__list">
            {draft.sharedWith.map((c) => (
              <li key={c.uid} className="dft__collab">
                <span className="dft__collab__uid">{collaboratorLabel(c.uid)}</span>
                <Pill variant="low">{c.permission}</Pill>
                <Button
                  variant="ghost"
                  onClick={() =>
                    unshare.mutate({ draftId: draft.id, uid: c.uid })
                  }
                  disabled={unshare.isPending}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="dft__form__actions">
        <DialogClose asChild>
          <Button variant="ghost">Done</Button>
        </DialogClose>
      </div>
    </Dialog>
  );
}

// Minimal shape of the CKEditor instance bits we use to locate + scroll to a
// clause. Typed structurally so we don't couple the screen to the CKEditor
// build (which lives behind the lazy editor chunk).
type CkWriter = {
  setSelection: (selectable: unknown, placeOrOffset?: unknown) => void;
  createElement: (name: string, attributes?: Record<string, unknown>) => unknown;
  insertText: (text: string, node: unknown, offset?: number) => void;
  append: (node: unknown, parent: unknown) => void;
};
export type CkEditorLike = {
  execute: (command: string, ...args: unknown[]) => unknown;
  model: {
    change: (cb: (writer: CkWriter) => void) => void;
    document: { getRoot: () => unknown };
    // Inserts content at the current selection (the user's cursor) by default.
    insertContent: (content: unknown, selectable?: unknown) => void;
  };
  editing: { view: { scrollToTheSelection: () => void; focus: () => void } };
};

// [placement fix, found live 2026-08-14] The block a missing clause should be
// inserted AFTER, located from the finding's own section label ("§5
// Compensation"). Unique match only — the same discipline every other anchor in
// this file follows. Returns null when there is no label, the label isn't in the
// draft (the usual case for a genuinely absent clause), or it appears more than
// once; the caller then appends at the document end.
// (Returns `unknown` — the block element is opaque here, exactly like every
// other selectable this file hands back to CKEditor. `null` means "no anchor".)
function sectionAnchorBlock(
  editor: CkEditorLike,
  section: string | undefined
): unknown {
  const label = normalizeText(section);
  if (label.length === 0) return null;
  let res: unknown;
  try {
    res = editor.execute("find", label);
  } catch {
    return null; // find command not present
  }
  const results = (
    res as {
      results?: {
        length: number;
        get: (i: number) => { marker?: { getRange?: () => unknown } };
      };
    }
  ).results;
  if (results === undefined || results.length !== 1) return null;
  try {
    // A Position's `parent` is the block element containing the match, so
    // setSelection(parent, "after") lands cleanly between blocks rather than
    // splitting the matched paragraph in half.
    const range = results.get(0).marker?.getRange?.() as
      | { start?: { parent?: unknown } }
      | undefined;
    return range?.start?.parent ?? null;
  } catch {
    return null;
  }
}

// Apply a finding through the editor's MODEL (find→replace command / writer),
// so the change is a normal undoable operation (Ctrl+Z works) — unlike
// rewriting the HTML and remounting, which wipes the undo stack. Returns false
// if the editor can't perform it (caller falls back to the HTML path).
export function applyViaEditor(editor: CkEditorLike, finding: DraftReviewFinding): boolean {
  const standard = (finding.templateClause ?? "").trim();
  const hasClause = (finding.contractClause ?? "").trim().length > 0;
  // Only append when the clause is truly absent (no draft anchor). When the
  // draft has a placeholder/partial line, fall through and replace it in place.
  if (finding.kind === "missing" && !hasClause) {
    if (standard.length === 0) return false;
    try {
      // [placement fix, found live 2026-08-14] Placement must NOT depend on
      // where the caret happens to be. This used to call insertContent(p) with
      // no position, which inserts at the current selection — the intent was
      // "the attorney controls placement", but in practice the attorney had
      // clicked somewhere in the document while reading it, so an applied
      // recommendation landed in whatever section the cursor was left in.
      // Nothing about a click while READING says anything about where a clause
      // belongs. Deterministic instead: insert after the block the finding's
      // own section label resolves to, and failing that append at the document
      // end — the same contract applyFindingToHtml has always had for this
      // case. Then select + scroll so they see exactly where it landed.
      const anchor = sectionAnchorBlock(editor, finding.section);
      // [draft numbering] After a list item, the clause is the next list item (same
      // type and indent, its own id) — a plain paragraph there would break the list.
      const get = (anchor as { getAttribute?: (k: string) => unknown } | null)?.getAttribute;
      const listType = get?.call(anchor, "listType");
      const listAttrs =
        listType !== undefined
          ? { listType, listIndent: get?.call(anchor, "listIndent") ?? 0, listItemId: `li-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}` }
          : undefined;
      editor.model.change((writer) => {
        const p = writer.createElement("paragraph", listAttrs);
        writer.insertText(standard, p);
        if (anchor !== null) writer.setSelection(anchor, "after");
        else writer.setSelection(editor.model.document.getRoot(), "end");
        editor.model.insertContent(p);
        writer.setSelection(p, "on");
      });
      editor.editing.view.scrollToTheSelection();
      editor.editing.view.focus();
      return true;
    } catch {
      return false;
    }
  }
  const fragments = fragmentsOf(finding.contractClause);
  if (fragments.length === 0) return false;
  for (const frag of fragments) {
    let res: unknown;
    try {
      res = editor.execute("find", frag);
    } catch {
      return false;
    }
    const results = (res as { results?: { length: number; get: (i: number) => unknown } }).results;
    // Only act on an UNAMBIGUOUS match. A fragment that occurs in more than one
    // place can't be resolved to a single clause — taking the first hit is what
    // silently edited the wrong clause (S-481/482). Skip an ambiguous fragment
    // and try a more specific one; if none is unique, decline (the caller warns
    // the user) rather than mutate the wrong location.
    if (results !== undefined && results.length === 1) {
      const first = results.get(0);
      try {
        // added → delete the matched clause; substantive → swap in the standard.
        editor.execute("replace", finding.kind === "added" ? "" : standard, first);
      } catch {
        return false;
      }
      // Reveal where the change landed so the attorney can see it in context.
      // Best-effort — never fail the apply if the view isn't ready.
      try {
        editor.editing.view.scrollToTheSelection();
      } catch {
        /* non-fatal */
      }
      return true;
    }
  }
  return false;
}

function normalizeText(s: string | undefined): string {
  return (s ?? "").replace(/\s+/g, " ").trim();
}

// Candidate fragments to search for, FULL clause first (so a verbatim match
// highlights the whole clause, not a cut-off prefix), then progressively
// shorter prefixes trimmed at word boundaries as a tolerance fallback.
function fragmentsOf(text: string | undefined): string[] {
  // [draft numbering] A finding's clause text carries the numbers the reader sees
  // ("2. COMPENSATION. …"); a numbered-list draft's text has none (the editor
  // draws them). Searching without them finds both kinds of draft.
  const norm = normalizeText((text ?? "").replace(/^[ \t]*(?:\d+(?:\.\d+)*\.?|[A-Z]\.|\([a-z]{1,4}\))[ \t]+/gm, ""));
  if (norm.length === 0) return [];
  const out: string[] = [norm];
  for (const len of [120, 80, 50, 30]) {
    if (norm.length <= len) continue;
    let frag = norm.slice(0, len);
    const lastSpace = frag.lastIndexOf(" ");
    if (lastSpace > 12) frag = frag.slice(0, lastSpace);
    if (!out.includes(frag)) out.push(frag);
  }
  return out;
}

export function searchFragments(finding: DraftReviewFinding): string[] {
  // After a finding is applied the original draft clause may no longer be in the
  // document — "substantive" replaced it with the standard text and "missing"
  // inserted the standard, while "added" removed it outright. Searching for the
  // original contractClause then falsely reports "couldn't locate" (S-482), so
  // once applied we look for what is actually in the draft now.
  if (finding.status === "apply") {
    if (finding.kind === "added") return []; // removed — nothing to jump to
    const applied = fragmentsOf(finding.templateClause);
    if (applied.length > 0) return applied;
  }
  const fromClause = fragmentsOf(finding.contractClause);
  return fromClause.length > 0 ? fromClause : fragmentsOf(finding.section);
}

// Fold to a whitespace- and smart-quote-insensitive form for tolerant matching
// — the finding text and the rendered draft often differ only by run-length
// whitespace or curly vs straight quotes, which otherwise defeats a literal
// substring match.
function canonForMatch(s: string | undefined): string {
  return normalizeText(s)
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"');
}

// Locate via the editor model's `find` command, select the first match and
// scroll to it. The find feature also highlights the match. Returns false if
// the editor/command is unavailable or no fragment matches.
function findInEditor(editor: CkEditorLike | null, fragments: string[]): boolean {
  if (editor === null) return false;
  for (const frag of fragments) {
    let res: unknown;
    try {
      res = editor.execute("find", frag);
    } catch {
      return false; // find command not present
    }
    const results = (res as { results?: { length: number; get: (i: number) => { marker: { getRange: () => unknown } } } })
      .results;
    // Only jump on an unambiguous match — scrolling to the first of several
    // occurrences is the "clicked it and it landed on the wrong clause" bug.
    if (results !== undefined && results.length === 1) {
      const range = results.get(0).marker.getRange();
      editor.model.change((writer) => writer.setSelection(range));
      editor.editing.view.scrollToTheSelection();
      editor.editing.view.focus();
      return true;
    }
  }
  return false;
}

// Reverse of applyViaEditor — restore the original draft text for a finding
// that was applied. Undoable (model edit). Returns false if it can't.
export function undoViaEditor(editor: CkEditorLike, finding: DraftReviewFinding): boolean {
  const standard = (finding.templateClause ?? "").trim();
  const original = (finding.contractClause ?? "").trim();
  const hasClause = original.length > 0;
  if (finding.kind === "added") {
    // Apply removed the clause as a single undoable model change. Reverse it
    // through the editor's OWN undo so the clause returns to its original
    // position. The previous manual re-append dropped it at the document end —
    // i.e. below the signature block — and could double-insert when the find
    // matched only a prefix fragment (S-481). Caveat: this reverts the latest
    // editor change, so undoing several findings is last-in-first-out; out of
    // order, fall through to the manual-edit hint.
    if (original.length === 0) return false;
    try {
      editor.execute("undo");
      return true;
    } catch {
      return false;
    }
  }
  // Find the standard text we inserted, then either remove it (it was appended
  // for a truly-absent clause) or swap the original draft text back in.
  const appended = finding.kind === "missing" && !hasClause;
  const fragments = fragmentsOf(standard);
  if (fragments.length === 0) return false;
  for (const frag of fragments) {
    let res: unknown;
    try {
      res = editor.execute("find", frag);
    } catch {
      return false;
    }
    const results = (res as { results?: { length: number; get: (i: number) => unknown } }).results;
    // Unique match only — same rule as apply, so undo never swaps back over the
    // wrong occurrence of the standard text.
    if (results !== undefined && results.length === 1) {
      const first = results.get(0);
      try {
        editor.execute("replace", appended ? "" : original, first);
      } catch {
        return false;
      }
      return true;
    }
  }
  return false;
}

// Fallback: scan the rendered editor DOM for the fragment and scroll/flash it.
function findInDom(fragments: string[]): boolean {
  const root = document.querySelector(".dft__detail__main .ck-content");
  if (root === null) return false;
  for (const frag of fragments) {
    const needle = canonForMatch(frag);
    if (needle.length === 0) continue;
    // Collect the distinct blocks that contain the fragment. Only jump when
    // exactly one block matches — scrolling to the first of several is the
    // "clicked it and landed on the wrong clause" bug. Otherwise try a more
    // specific fragment, then give up (caller warns).
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const hits = new Set<Element>();
    let node = walker.nextNode();
    while (node !== null) {
      if (canonForMatch(node.textContent ?? "").includes(needle)) {
        const el = node.parentElement;
        if (el !== null) hits.add(el);
      }
      node = walker.nextNode();
    }
    if (hits.size === 1) {
      const el = [...hits][0]!;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("dft__jump-flash");
      window.setTimeout(() => el.classList.remove("dft__jump-flash"), 1600);
      return true;
    }
  }
  return false;
}

// DOM twin of sectionAnchorBlock — the element a missing clause goes after.
// Unique match only; null means "append at the end" (see applyFindingToHtml).
function sectionAnchorElement(
  body: HTMLElement,
  section: string | undefined
): Element | null {
  const label = canonForMatch(section);
  if (label.length === 0) return null;
  const BLOCKS = "p, li, div, h1, h2, h3, h4, h5, h6, blockquote";
  // Innermost blocks only — an <li> and its <p> are one clause, not two matches.
  const blocks = Array.from(body.querySelectorAll(BLOCKS)).filter((b) => b.querySelector(BLOCKS) === null);
  const matches = blocks.filter((b) =>
    canonForMatch(b.textContent ?? "").includes(label)
  );
  return matches.length === 1 ? matches[0]! : null;
}

// Apply a finding's recommendation to the draft HTML. Returns the new HTML, or
// null if the targeted clause can't be located (so the caller can warn).
//   - missing:     insert the certified-standard clause as a new paragraph,
//                  after its section if that resolves, else at the end
//   - added:       remove the block that contains the draft clause
//   - substantive: replace the draft clause text with the certified-standard
// Operates on a parsed DOM so we edit a single block, not the whole document.
export function applyFindingToHtml(
  html: string,
  finding: DraftReviewFinding
): string | null {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const body = doc.body;
  const clause = (finding.contractClause ?? "").trim();
  const standard = (finding.templateClause ?? "").trim();

  if (finding.kind === "missing" && clause.length === 0) {
    if (standard.length === 0) return null;
    const p = doc.createElement("p");
    p.textContent = standard;
    // [placement fix, found live 2026-08-14] Same rule as the editor path, so a
    // finding doesn't land in a different place depending on which path ran:
    // after its own section when that resolves to exactly one block, otherwise
    // appended at the end (this path's long-standing behavior).
    const anchor = sectionAnchorElement(body, finding.section);
    // [draft numbering] In a numbered-list draft the new clause is the next list item, so it is numbered too.
    const item = anchor?.closest("li") ?? null;
    if (item !== null) {
      const li = doc.createElement("li");
      li.appendChild(p);
      item.after(li);
    } else if (anchor !== null) anchor.after(p);
    else body.appendChild(p);
    return body.innerHTML;
  }

  if (clause.length === 0) return null;
  const BLOCKS = "p, li, div, h1, h2, h3, h4, h5, h6, blockquote";
  // [draft numbering] Innermost blocks only: in a numbered-list draft an <li>
  // and the <p> inside it hold the same clause, which read as ambiguous.
  const blocks = Array.from(body.querySelectorAll(BLOCKS)).filter((b) => b.querySelector(BLOCKS) === null);
  // Match on normalized text (collapse whitespace, lowercase) and try the full
  // clause first, then shorter prefixes — same tolerance as the jump search.
  // Match against block text with <br> treated as a space (textContent drops
  // <br> with no separator, which would defeat the match on older drafts).
  const blockText = (b: Element): string =>
    normalizeText(b.innerHTML.replace(/<br\s*\/?>/gi, " ").replace(/<[^>]+>/g, " ")).toLowerCase();
  const fragments = fragmentsOf(clause).map((f) => f.toLowerCase());
  let target: Element | null = null;
  for (const frag of fragments) {
    // Unique block match only — if a fragment hits more than one block we can't
    // tell which clause the finding meant, so try a more specific fragment and,
    // failing that, decline (return null → caller warns) instead of editing the
    // first block that happens to contain a shared phrase (S-481/482).
    const matches = blocks.filter((b) => blockText(b).includes(frag));
    if (matches.length === 1) {
      target = matches[0]!;
      break;
    }
  }
  if (target === null) return null;

  if (finding.kind === "added") {
    // A clause removed from a numbered list takes its (now empty) item with it, so the list renumbers.
    let gone: Element | null = target;
    while (gone.parentElement !== null && gone.parentElement !== body && /^(LI|OL|UL)$/.test(gone.parentElement.tagName) && gone.parentElement.children.length === 1) {
      gone = gone.parentElement;
    }
    gone.remove();
    return body.innerHTML;
  }
  // substantive — replace the clause text with the certified-standard text.
  if (standard.length === 0) return null;
  // [draft numbering] A clause's leading bold title ("<strong>GOVERNING LAW.</strong>") stays.
  const lead = target.firstElementChild;
  const title =
    lead !== null && /^(STRONG|B)$/.test(lead.tagName) && target.firstChild === lead && /[.:]\s*$/.test(lead.textContent ?? "")
      ? lead
      : null;
  const t = (target.textContent ?? "").slice(title?.textContent?.length ?? 0);
  // Prefer an in-place substring swap; otherwise replace the whole matched
  // block (it is essentially the clause for our paragraph-split drafts).
  const next = t.includes(clause) ? t.replace(clause, standard) : standard;
  if (title === null) {
    target.textContent = next;
  } else {
    target.replaceChildren(title, doc.createTextNode(` ${next.trim()}`));
  }
  return body.innerHTML;
}

// The action verb on a finding's primary button. The effect depends on the
// deviation kind — a "missing" clause is added, an "added" clause is removed,
// a "substantive" drift is replaced — so the label must say which, otherwise
// "Apply" on an added-clause finding reads as "insert" when it actually deletes
// (S-480).
export function applyVerb(kind: DraftReviewFinding["kind"]): string {
  switch (kind) {
    case "missing":
      return "Add to draft";
    case "added":
      return "Remove from draft";
    default:
      return "Replace with standard";
  }
}

function prettyGuild(g: string): string {
  return g === "SAG" ? "SAG-AFTRA" : g;
}

function prettyDealType(d: string): string {
  return d.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
